/**
 * IP Geolocation Service
 * Provides fallback language detection when browser language is not available
 */

// Free IP geolocation API (no API key required, but has rate limits)
const IP_GEOLOCATION_API = "https://ipapi.co/json/";

/**
 * Get user's location information from IP address
 * @returns {Promise<Object>} Location data including country code
 */
export const getLocationFromIP = async () => {
  try {
    const response = await fetch(IP_GEOLOCATION_API, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      country: data.country_code,
      countryName: data.country_name,
      region: data.region,
      city: data.city,
      timezone: data.timezone,
      languages: data.languages,
    };
  } catch (error) {
    console.warn("Failed to get location from IP:", error);
    return null;
  }
};

/**
 * Map country codes to language codes
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {string} Language code or null if not mapped
 */
export const getLanguageFromCountry = (countryCode) => {
  const countryToLanguage = {
    // Portuguese speaking countries
    BR: "pt", // Brazil
    PT: "pt", // Portugal
    AO: "pt", // Angola
    MZ: "pt", // Mozambique
    CV: "pt", // Cape Verde
    GW: "pt", // Guinea-Bissau
    ST: "pt", // São Tomé and Príncipe
    TL: "pt", // Timor-Leste

    // English speaking countries (fallback)
    US: "en", // United States
    GB: "en", // United Kingdom
    CA: "en", // Canada
    AU: "en", // Australia
    NZ: "en", // New Zealand
    IE: "en", // Ireland
    ZA: "en", // South Africa
  };

  return countryToLanguage[countryCode] || null;
};

/**
 * Detect language from IP location with fallback
 * @returns {Promise<string>} Detected language code
 */
export const detectLanguageFromIP = async () => {
  try {
    const location = await getLocationFromIP();

    if (location && location.country) {
      const language = getLanguageFromCountry(location.country);
      if (language) {
        return language;
      }
    }

    // Fallback to English if country not mapped
    return "en";
  } catch (error) {
    console.warn("Failed to detect language from IP:", error);
    return "en";
  }
};

/**
 * Enhanced language detection with IP fallback
 * @returns {Promise<string>} Detected language code
 */
export const detectUserLanguageWithIPFallback = async () => {
  // 1. Check navigator.language (most reliable)
  const navigatorLang = navigator.language || navigator.userLanguage;
  if (navigatorLang) {
    const lang = navigatorLang.split("-")[0]; // Get primary language (e.g., "pt" from "pt-BR")
    const availableLanguages = ["en", "pt"]; // Add more languages here as needed
    if (availableLanguages.includes(lang)) {
      return lang;
    }
  }

  // 2. Check browser language preferences
  if (navigator.languages && navigator.languages.length > 0) {
    const availableLanguages = ["en", "pt"];
    for (const lang of navigator.languages) {
      const primaryLang = lang.split("-")[0];
      if (availableLanguages.includes(primaryLang)) {
        return primaryLang;
      }
    }
  }

  // 3. Check localStorage for saved preference
  const savedLang = localStorage.getItem("gg-language");
  const availableLanguages = ["en", "pt"];
  if (savedLang && availableLanguages.includes(savedLang)) {
    return savedLang;
  }

  // 4. IP geolocation fallback
  try {
    const ipLanguage = await detectLanguageFromIP();
    return ipLanguage;
  } catch (error) {
    console.warn("IP geolocation failed:", error);
  }

  // 5. Default to English
  return "en";
};
